import { z } from "zod";
import {
  AI_CHIP_KINDS,
  AI_PAGE_ID_MAX_CHARS,
  AI_PAGE_ID_RE,
  AI_SLOT_ID_MAX_CHARS,
  AI_SLOT_ID_RE,
  AI_MENU_MAX_CHIPS,
  AI_MENU_SECTIONS,
  AI_SLOT_FILE_IMPORT_KINDS,
  AI_SLOT_TYPES,
  AI_TASK_STATES,
  AI_TURN_MAX_CHIPS,
  AI_TURN_TYPES,
} from "@goppy/domain";
import { AI_INPUT_MAX_CHARS } from "./envelope.js";

/**
 * 🔴 US-AIV2-0023 (@UC0163 @UC0169 · bàn giao 0024 #810) — HỢP ĐỒNG LƯỢT trợ lý v2 trên dây, phía TS.
 *
 * Hình dây do mobile khai trước (`apps/mobile/lib/features/ai_chat/domain/entities/ai_turn.dart`); nguồn kiểu phía server là domain
 * `ai/templates/render-turn.ts` (`AI_TURN_TYPES` · `AI_CHIP_KINDS` · hai trần chip — RE-EXPORT ở đây, không chép). Fixture
 * ngôn-ngữ-trung-tính `./fixtures/turns/*.json` là NGUỒN parity ba bên: vitest (`turn.test.ts` parse bằng schema này), Dart
 * (`ai_turn_test.dart` đọc cùng file), và domain (`renderConfirm`… phải parse qua schema này).
 *
 *  - `.strict()` mọi tầng: server đời sau thêm field ⇒ test TS đỏ trước khi mobile im lặng bỏ qua.
 *  - `kind` là tập ĐÓNG: mobile hardcode `open_screen` (`ai_chip.dart`), đổi tên wire ở TS mà không đỏ ⇒ client gửi chip lên server thay vì mở
 *    màn (reviewer #810). `primary: true` do SERVER đặt — client không suy nút chính từ `kind`.
 *  - Payload chip CHỈ CHUỐI (LLM/JSON không mang số — `@UC0151`); `version` của chip `confirm` là chuỗi số của `ai_tasks.version` (0021
 *    `expectedVersion`), server parse lại.
 *  - `taskId` uuid nibble `[1-8]` (uuid v7 của `ai_tasks`).
 */
export { AI_CHIP_KINDS, AI_MENU_MAX_CHIPS, AI_MENU_SECTIONS, AI_TURN_MAX_CHIPS, AI_TURN_TYPES };

/**
 * 🔴🔴 US-AIV2-0076 — TRẦN CHIP TRÊN DÂY: **DẪN XUẤT**, không khai tay.
 *
 * Server có **hai** trần UX với hai lý do khác nhau — ô chọn (`AI_TURN_MAX_CHIPS` = 9 enum + Huỷ) và menu khám phá
 * (`AI_MENU_MAX_CHIPS` = số mục catalog menu + chỗ nút dẫn — AI-MENU-A, dẫn xuất). Hợp đồng dây thì chỉ cần biết **lượt dài nhất hợp lệ
 * là bao nhiêu**, nên nó lấy `Math.max` của hai trần ấy thay vì mang một con số thứ ba.
 *
 * 🔴 **KHÍT 0 DƯ, và đó là CỐ Ý** (reviewer PR #882, L-3): trần dây bằng ĐÚNG trần UX lớn hơn, không cộng biên an toàn.
 * Biên an toàn ở đây sẽ là một chỗ cho lượt dài hơn trần UX **lọt qua mà không ai biết**; khít 0 dư nghĩa là chip thứ N+1
 * là một lỗi 400 nhìn thấy được ngay ở test, không phải một hành vi lặng. Hệ quả cần biết khi nâng ngân sách chip
 * (thêm mục catalog · thêm chỗ nút dẫn): phải nâng trần UX, **không** được "chỉ nới trần dây cho qua".
 *
 * Vì sao BẮT BUỘC phải dẫn xuất: khai tay ở đây là **cùng một luật sống ở hai bản chép**, và bản chép sẽ mục đúng lúc trần UX
 * nhích lên — hậu quả không phải một menu ngắn, mà **`400` ở biên**: lượt chào bị chính hợp đồng của mình từ chối **trước khi
 * tới người dùng**. Dẫn xuất làm trạng thái đó **không biểu diễn được**, thay vì phải nhớ nâng cả hai.
 */
export const AI_TURN_CHIPS_MAX = Math.max(AI_TURN_MAX_CHIPS, AI_MENU_MAX_CHIPS);
export type { AiChipKind, AiTurnType } from "@goppy/domain";

export const AI_TURN_TEXT_MAX_CHARS = 2000;
export const AI_CHIP_LABEL_MAX_CHARS = 80;
export const AI_CHIP_PAYLOAD_KEY_MAX_CHARS = 32;
export const AI_CHIP_PAYLOAD_VALUE_MAX_CHARS = 400;
export const AI_TURN_CARD_MAX_LINES = 24;
/** GUIDE-FIX S2 (@UC0368): trần số dòng của `lineCores` — lời chào là vài thẻ, một dòng mỗi thẻ; trần chỉ chặn payload phình vô hạn. */
export const AI_TURN_LINE_CORES_MAX = 24;
/** Mã lỗi sản phẩm: HOA, bắt đầu bằng chữ, ≤64 ký tự — cùng dạng `PRODUCT_CODE_RE` ở cửa api (`error-code.ts`). */
export const AI_FAILURE_CODE_RE = /^[A-Z][A-Z0-9_]{0,63}$/;
/** Trần câu người dùng gõ ở cửa HTTP — cùng hằng với cổng LLM (0051/0012), một nguồn. */
export const AI_TURN_INPUT_MAX_CHARS = AI_INPUT_MAX_CHARS;

const uuidV1to8 = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
const pageId = z.string().max(AI_PAGE_ID_MAX_CHARS).regex(AI_PAGE_ID_RE);
const slotId = z.string().max(AI_SLOT_ID_MAX_CHARS).regex(AI_SLOT_ID_RE);

export const aiChipSchema = z
  .object({
    kind: z.enum(AI_CHIP_KINDS),
    label: z.string().min(1).max(AI_CHIP_LABEL_MAX_CHARS),
    payload: z.record(z.string().min(1).max(AI_CHIP_PAYLOAD_KEY_MAX_CHARS), z.string().max(AI_CHIP_PAYLOAD_VALUE_MAX_CHARS)).readonly().default({}),
    primary: z.literal(true).optional(),
    /**
     * AI-MENU-A (@UC0353 · GOPPY-45 Q2) — MỤC của nút trên màn Khám phá (catalog menu, ba khoá ĐÓNG của domain). Vắng ⇒ app xếp «Việc khác»
     * (lượt cũ đã lưu trong `ai_messages` · nút thẻ trỏ trang ngoài catalog). Chỉ chiều server → client: chip gửi lên KHÔNG mang trường này.
     */
    section: z.enum(AI_MENU_SECTIONS).optional(),
  })
  .strict();
export type AiChip = z.infer<typeof aiChipSchema>;

export const aiTurnCardLineSchema = z.object({ label: z.string().min(1).max(60), value: z.string().min(1).max(AI_CHIP_PAYLOAD_VALUE_MAX_CHARS) }).strict();
export const aiTurnCardSchema = z
  .object({ title: z.string().min(1).max(120).optional(), lines: z.array(aiTurnCardLineSchema).min(1).max(AI_TURN_CARD_MAX_LINES).readonly() })
  .strict();

export const aiTurnSchema = z
  .object({
    type: z.enum(AI_TURN_TYPES),
    text: z.string().min(1).max(AI_TURN_TEXT_MAX_CHARS),
    chips: z.array(aiChipSchema).max(AI_TURN_CHIPS_MAX).readonly().default([]),
    taskId: uuidV1to8.optional(),
    taskState: z.enum(AI_TASK_STATES).optional(),
    page: pageId.optional(),
    /** `fileImport` (US-AIV2-0121 · @UC0390): ô này nhận TỆP danh sách ⇒ app hiện nút đính kèm, gửi `POST /v1/ai/v2/tasks/:taskId/file`. */
    slot: z.object({ id: slotId, type: z.enum(AI_SLOT_TYPES), fileImport: z.enum(AI_SLOT_FILE_IMPORT_KINDS).optional() }).strict().optional(),
    card: aiTurnCardSchema.optional(),
    /**
     * 🔴 US-AIV2-0066 (@UC0219) — MỘT dòng nhắc trên phiếu xác nhận, hiển thị RIÊNG (⚠️), không trộn
     * vào `text`. **Máy cảnh báo, người quyết**: trường này không đổi `chips`, không chặn `confirm`.
     *
     * Không có nó ở đây thì `.strict()` chặn ngay ở biên và cảnh báo **không bao giờ tới mobile** —
     * đúng thứ chú thích đầu file cảnh báo ("server đời sau thêm field ⇒ test TS đỏ trước khi mobile
     * im lặng bỏ qua").
     */
    warning: z.string().min(1).max(AI_TURN_TEXT_MAX_CHARS).optional(),
    /**
     * 🔴 US-AIV2-0109 S01 (@UC0237) — MÃ LỖI ổn định của một lượt THẤT BẠI, cho MÁY đọc (test · người vận hành · client map
     * `code→message` nếu muốn). Câu chữ ở `text` là cho NGƯỜI và KHÔNG chứa mã (CLAUDE.md §7). Chỉ có ở lượt `answer`.
     */
    failureCode: z.string().regex(AI_FAILURE_CODE_RE).optional(),
    /**
     * 🔴 GUIDE-FIX-20261020 S2 (@UC0368 · G-5 APP-GUIDE-2) — TẬP lõi câu (KHÔNG theo chỉ số dòng — server có thể chèn dòng khác vào `text`) của các dòng
     * mang GIỌNG dẫn đường (lời mở «Chào bạn!» · câu nối «Ngoài ra, …» — `guideVoiceText`), tách bằng `guideLineCore` của domain (một nguồn), không trùng,
     * tối đa `AI_TURN_LINE_CORES_MAX`. Chỉ cho MÁY đọc, không hiển thị: app so lượt
     * LÀM MỚI (`GET …/guide` trả câu thẻ gốc, không giọng) với lượt đang hiện theo cả dòng lẫn lõi — client KHÔNG chép hằng lời mở/câu nối. Vắng =
     * không dòng nào mang giọng (hoặc server cũ) ⇒ app so nguyên văn như cũ.
     */
    lineCores: z.array(z.string().max(AI_TURN_TEXT_MAX_CHARS)).max(AI_TURN_LINE_CORES_MAX).optional(),
  })
  .strict()
  .superRefine((t, ctx) => {
    // Cảnh báo CHỈ có nghĩa ở lượt xác nhận: nó là thứ giúp người ta quyết TRƯỚC khi bấm. Dán vào
    // một lượt trả lời hay lượt hỏi ô là nói về một quyết định chưa tồn tại.
    if (t.failureCode !== undefined && t.type !== "answer") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["failureCode"], message: "mã lỗi chỉ có ở lượt answer" });
    }
    if (t.warning !== undefined && t.type !== "confirm") {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["warning"], message: "cảnh báo chỉ có ở lượt confirm" });
    }
    if (t.type === "confirm" && !t.chips.some((c) => c.kind === "confirm")) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["chips"], message: "lượt confirm phải có chip confirm" });
    }
    if (t.type === "redirect" && t.chips.some((c) => c.kind === "confirm")) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["chips"], message: "redirect không có nút Đồng ý (T3 — @UC0155)" });
    }
    if (t.type === "ask_slot" && t.slot === undefined) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["slot"], message: "ask_slot phải nói đang hỏi ô nào" });
    }
    if (t.chips.filter((c) => c.primary).length > 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["chips"], message: "tối đa một nút chính" });
    }
  });
export type AiTurn = z.infer<typeof aiTurnSchema>;

/**
 * Chip client GỬI LÊN: nguyên `{kind,payload}` server đã đưa — trừ `open_screen` (client tự mở, không gửi). KHÔNG `label`/`primary`/`section`:
 * đó là chữ trình bày của server, server không đọc lại (AI-MENU-A: nút họ gửi `payload.family` như mọi chip `choose`) ⇒ `.strict()` trả 400.
 */
export const aiTurnRequestChipSchema = z
  .object({
    kind: z.enum(AI_CHIP_KINDS.filter((k): k is Exclude<(typeof AI_CHIP_KINDS)[number], "open_screen"> => k !== "open_screen") as [Exclude<(typeof AI_CHIP_KINDS)[number], "open_screen">, ...Exclude<(typeof AI_CHIP_KINDS)[number], "open_screen">[]]),
    payload: z.record(z.string().min(1).max(AI_CHIP_PAYLOAD_KEY_MAX_CHARS), z.string().max(AI_CHIP_PAYLOAD_VALUE_MAX_CHARS)).readonly().default({}),
  })
  .strict();

/**
 * Thân `POST /v1/ai/v2/turns` — ĐÚNG MỘT trong `text` (câu gõ) hoặc `chip` (nút bấm). `conversationId` vắng ⇒ server mở hội thoại mới.
 * `text` cap theo `AI_TURN_INPUT_MAX_CHARS` (ReDoS/cost — bàn giao 0022); `groupId` là nhóm HIỆU LỰC của lượt (null = chưa có nhóm).
 */
export const aiTurnRequestSchema = z
  .object({
    conversationId: uuidV1to8.optional(),
    groupId: uuidV1to8.nullable().optional(),
    text: z.string().min(1).max(AI_TURN_INPUT_MAX_CHARS).optional(),
    chip: aiTurnRequestChipSchema.optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    if ((r.text === undefined) === (r.chip === undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["text"], message: "đúng một trong text hoặc chip" });
    }
  });
export type AiTurnRequest = z.infer<typeof aiTurnRequestSchema>;

/**
 * 🔴 US-AIV2-0024d vế (a) (@UC9881) — PHẠM VI hiện hành, để client vẽ **chip phạm vi** trên thanh đầu
 * màn chat (*"Tất cả nhóm"* / *"Đang trong: Lớp 4B"*).
 *
 * 🔴 `label` do **SERVER** đặt, client **KHÔNG tự ghép chữ** — hai bề mặt soạn hai câu cho cùng một
 * trạng thái là hai sự thật, và cái thứ hai sẽ lệch dần (luật gốc của `@UC9881` từ v1).
 * 🔴 `groupId: null` = tab TOÀN CỤC (chưa chốt nhóm nào), KHÁC với "nhóm tên là null".
 * 🔴 `canChange` = có gì để đổi không (người 1 nhóm thì đổi sang đâu?) — server biết, client thì không.
 */
/**
 * 🔴 US-AIV2-0119 S02 (@UC9881) — trần số nhóm trong dropdown chọn nhóm. Người thật có vài nhóm; trần chỉ chặn payload phình vô hạn.
 */
export const AI_SCOPE_OPTIONS_MAX = 50;

/**
 * 🔴 US-AIV2-0119 S02 (@UC9881) — một mục của dropdown chọn nhóm: id + TÊN nhóm, KHÔNG mang số quỹ/đợt/thành viên (P5.3, cùng luật chip
 * hỏi nhóm ở lời chào). `groupId` không null: "Tất cả nhóm" không phải một mục để chọn.
 */
export const aiScopeOptionSchema = z.object({ groupId: z.string().uuid(), label: z.string().min(1).max(80) }).strict();

export const aiContextScopeSchema = z
  .object({
    groupId: z.string().uuid().nullable(),
    label: z.string().min(1).max(80),
    canChange: z.boolean(),
    // US-AIV2-0119 S02: các nhóm người này đổi sang được (CHÍNH danh sách server dựng `canChange`). Vắng = server cũ ⇒ app giữ đường cũ.
    options: z.array(aiScopeOptionSchema).max(AI_SCOPE_OPTIONS_MAX).optional(),
  })
  .strict();

/**
 * Phản hồi: lượt + hội thoại (client giữ để gửi lượt sau).
 * 🔴 US-AIV2-0111 (review #1027, @UC9881): `scope` = PHẠM VI của hội thoại SAU lượt này (cùng hình `scope` của `GET /context`) — app vẽ
 * chip phạm vi từ server, bỏ đường tự suy từ chip `choose` vừa bấm (suy ở client = sự thật thứ hai). Tuỳ chọn: app cũ bỏ qua; server
 * đọc tên nhóm hỏng ⇒ bỏ trường (không bịa nhãn).
 */
export const aiTurnResponseSchema = z.object({ conversationId: uuidV1to8, turn: aiTurnSchema, scope: aiContextScopeSchema.optional() }).strict();
export type AiTurnResponse = z.infer<typeof aiTurnResponseSchema>;
